import { Calendar, CheckCircle2, Circle, PanelRightClose, Ticket } from 'lucide-react';
import { DateTime } from 'luxon';
import { useState } from 'react';
import type { BusinessObjectMap } from '@Types/soul';
import DraftEventImages from './DraftEventImages';

interface DraftEvent {
  name?: string;
  description?: string;
  teaser?: string;
  startDateTime?: string;
  endDateTime?: string;
  venueId?: string;
  venueName?: string;
  capacity?: number;
  visibility?: 'PUBLIC' | 'MEMBERS_ONLY' | 'PRIVATE';
  status?: string;
  ticketTypes?: { name: string; description: string; price: number; available: number }[];
  featuredImageId?: string;
  imageIds?: string[];
}

interface Props {
  businessObjects: BusinessObjectMap;
  onClose: () => void;
  onUpdateDraft: (patch: { featuredImageId?: string; imageIds?: string[] }) => void;
}

interface FieldRowProps {
  label: string;
  value?: string | number | null;
  filled: boolean;
  /** When true, long text truncates to one line and expands on click. */
  expandable?: boolean;
}

function FieldRow({ label, value, filled, expandable }: FieldRowProps) {
  const [expanded, setExpanded] = useState(false);
  const text = value != null ? String(value) : '';
  const isLong = expandable && text.length > 60;

  return (
    <div className="flex items-start gap-2 py-1.5">
      {filled ? (
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
      ) : (
        <Circle className="mt-0.5 h-4 w-4 shrink-0 text-zinc-300" />
      )}
      <div className="min-w-0 flex-1">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
          {label}
        </span>
        {filled && value != null ? (
          isLong ? (
            <button
              type="button"
              onClick={() => setExpanded((p) => !p)}
              className="block w-full text-left"
              title={expanded ? 'Click to collapse' : 'Click to expand'}
            >
              <p
                className={
                  expanded
                    ? 'whitespace-pre-wrap break-words text-sm text-zinc-800'
                    : 'truncate text-sm text-zinc-800'
                }
              >
                {text}
              </p>
              <span className="text-[11px] font-medium text-rose-500">
                {expanded ? 'Show less' : 'Show more'}
              </span>
            </button>
          ) : (
            <p className="whitespace-pre-wrap break-words text-sm text-zinc-800">
              {text}
            </p>
          )
        ) : (
          <p className="text-sm text-zinc-400 italic">Not set yet</p>
        )}
      </div>
    </div>
  );
}

function formatDate(iso?: string): string | undefined {
  if (!iso) return undefined;
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? dt.toFormat('LLL d, yyyy h:mm a') : iso;
}

export default function DraftEventPanel({
  businessObjects,
  onClose,
  onUpdateDraft,
}: Props) {
  const draft = businessObjects['draftEvent']?.data as DraftEvent | undefined;

  if (!draft) return null;

  const hasTickets = Array.isArray(draft.ticketTypes) && draft.ticketTypes.length > 0;

  const REQUIRED_FIELDS: {
    label: string;
    key: keyof DraftEvent;
    display?: string;
    expandable?: boolean;
  }[] = [
    { label: 'Event Name', key: 'name', display: draft.name },
    { label: 'Start', key: 'startDateTime', display: formatDate(draft.startDateTime) },
    { label: 'End', key: 'endDateTime', display: formatDate(draft.endDateTime) },
    { label: 'Venue', key: 'venueId', display: draft.venueName ?? draft.venueId },
    { label: 'Capacity', key: 'capacity', display: draft.capacity != null ? String(draft.capacity) : undefined },
    { label: 'Visibility', key: 'visibility', display: draft.visibility },
    {
      label: 'Description',
      key: 'description',
      display: draft.description ? draft.description.replace(/<[^>]+>/g, '') : undefined,
      expandable: true,
    },
    { label: 'Teaser', key: 'teaser', display: draft.teaser, expandable: true },
  ];

  const filledCount = REQUIRED_FIELDS.filter((f) => {
    const v = draft[f.key];
    return v !== undefined && v !== null && v !== '';
  }).length + (hasTickets ? 1 : 0);
  const total = REQUIRED_FIELDS.length + 1; // +1 for ticket types

  return (
    <div className="flex w-72 shrink-0 flex-col border-l border-zinc-200 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-zinc-200 px-3 py-2.5">
        <div className="flex items-center gap-2 text-sm font-semibold text-zinc-800">
          <Calendar className="h-4 w-4 text-rose-500" />
          Event Draft
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-zinc-400">
            {filledCount}/{total}
          </span>
          <button
            onClick={onClose}
            className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
            title="Close panel"
          >
            <PanelRightClose className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 w-full bg-zinc-100">
        <div
          className="h-full bg-rose-400 transition-all duration-500"
          style={{ width: `${Math.round((filledCount / total) * 100)}%` }}
        />
      </div>

      {/* Fields */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-zinc-400">
          Required
        </p>
        <div className="divide-y divide-zinc-50">
          {REQUIRED_FIELDS.map((f) => {
            const v = draft[f.key];
            return (
              <FieldRow
                key={f.key}
                label={f.label}
                value={f.display ?? (v != null ? String(v) : undefined)}
                filled={v !== undefined && v !== null && v !== ''}
                expandable={f.expandable}
              />
            );
          })}

          {/* Ticket types */}
          <div className="flex items-start gap-2 py-1.5">
            {hasTickets ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-500" />
            ) : (
              <Circle className="mt-0.5 h-4 w-4 shrink-0 text-zinc-300" />
            )}
            <div className="min-w-0 flex-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
                Ticket Types
              </span>
              {hasTickets ? (
                <div className="mt-1 flex flex-wrap gap-1">
                  {draft.ticketTypes!.map((t, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs text-rose-700"
                    >
                      <Ticket className="h-3 w-3" />
                      {t.name} — ${t.price} · {t.available} avail
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm italic text-zinc-400">Not set yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Images */}
        <p className="mb-2 mt-4 text-[11px] font-bold uppercase tracking-wide text-zinc-400">
          Images
        </p>
        <DraftEventImages
          featuredImageId={draft.featuredImageId}
          imageIds={draft.imageIds}
          onUpdate={onUpdateDraft}
        />

        {/* Optional */}
        <p className="mb-2 mt-4 text-[11px] font-bold uppercase tracking-wide text-zinc-400">
          Optional
        </p>
        <div className="divide-y divide-zinc-50">
          <FieldRow
            label="Status"
            value={draft.status ?? 'DRAFT'}
            filled={true}
          />
        </div>
      </div>

      {filledCount === total && (
        <div className="border-t border-green-100 bg-green-50 px-3 py-2 text-xs text-green-700">
          All fields collected — confirm in chat to create the event.
        </div>
      )}
    </div>
  );
}
